import React from "react";
import {
  SEO,
  PageLayout,
  MarkdownContent,
  MultipleComponentIterator,
  FadeReveal,
  HorizontalImageCard,
  InternalLink,
  Conditional,
  LocationName,
  FishermanIcon,
  SocialMediaV2,
  PaginatedGrid,
  multipleComponentIterator,
  SlideReveal,
  Image,
} from "@bluefin/components";
import { Grid, Header, Card } from "semantic-ui-react";
import { graphql } from "gatsby";

import WebsiteLayout from "../components/WebsiteLayout.jsx";
import {
  getComponentFiles,
  getComponentContentNodeContent,
  withNamedArgs,
  getBusinessFiles,
} from "../utils/utils";
import { createLocationSlug } from "@bluefin/gatsby-plugin-fisherman-page-creator";

export default class LocationsPage extends React.PureComponent {
  render() {
    const { data } = this.props;
    const {
      fishermanBusiness,
      fishermanBusinessWebsitePage,
      allFishermanBusinessLocation,
      allFishermanBusinessFile,
      instagramSocialMedia,
    } = data;

    return (
      <WebsiteLayout>
        <SEO
          businessName={fishermanBusiness.name}
          pageName={fishermanBusinessWebsitePage.title}
          description={fishermanBusinessWebsitePage.description}
          images={getComponentFiles({
            components: fishermanBusinessWebsitePage.components,
            componentName: "HeroElement",
            numToSelect: 1,
          })}
          title={fishermanBusinessWebsitePage.seoTitle}
          ogUrl={"https://www.musesalonandspa.com/locations/"}
        />
        <PageLayout className={"locations-page"} hero={null}>
          <Grid
            stackable={true}
            textAlign={"left"}
            className={"locations-content-container"}
          >
            <Grid.Column width={16}>
              <Header as={"h1"}>
                <span
                  className={"no-highlight"}
                  children={getComponentContentNodeContent({
                    components: fishermanBusinessWebsitePage.components,
                    componentId: "locations_header",
                  })}
                />
                <span
                  className={"highlight"}
                  children={getComponentContentNodeContent({
                    components: fishermanBusinessWebsitePage.components,
                    componentId: "locations_header_highlighted",
                  })}
                />
              </Header>
              <MarkdownContent
                className={"locations-description"}
                content={getComponentContentNodeContent({
                  components: fishermanBusinessWebsitePage.components,
                  componentId: "locations_description",
                })}
              />
              <Card.Group itemsPerRow={3}>
                <MultipleComponentIterator
                  iterator={allFishermanBusinessLocation.nodes}
                  components={[
                    {
                      component: (
                        <FadeReveal
                          className={"ui card"}
                          duration={1250}
                          triggerOnce={true}
                        />
                      ),
                      propMap: { key: "street" },
                      children: [
                        {
                          component: (
                            <HorizontalImageCard
                              as={InternalLink}
                              link={true}
                              event={{
                                category: "Locations",
                                action: "View Location Details",
                              }}
                              imagePosition={"left"}
                              imageAsBackground={true}
                            />
                          ),
                          propMap: {
                            "event.label": "name",
                            to: createLocationSlug,
                            image: withNamedArgs({
                              func: getBusinessFiles,
                              args: {
                                businessFiles: allFishermanBusinessFile.nodes,
                                numToSelect: 1,
                                filesField: "featuredImage",
                              },
                            }),
                          },
                          children: [
                            {
                              component: <Header />,
                              children: [
                                {
                                  component: <Conditional />,
                                  children: [
                                    {
                                      component: <Conditional.Condition />,
                                      propMap: { condition: "name" },
                                      children: [
                                        {
                                          component: <LocationName />,
                                          propMap: { locationName: "name" },
                                        },
                                      ],
                                    },
                                  ],
                                },
                              ],
                            },
                            {
                              component: <LocationName />,
                              propMap: {
                                "address.street": "street",
                                "address.city": "city",
                                "address.state": "state",
                                "address.zip": "zipCode",
                              },
                            },
                            {
                              component: (
                                <FishermanIcon
                                  iconName={"arrow-right"}
                                  className={"location-arrow-right"}
                                />
                              ),
                            },
                          ],
                        },
                      ],
                    },
                  ]}
                />
              </Card.Group>
              <div className={"locations-gallery-header-container"}>
                <Header
                  as={"h2"}
                  className={"locations-gallery-header"}
                  children={getComponentContentNodeContent({
                    components: fishermanBusinessWebsitePage.components,
                    componentId: "image_gallery_section_header",
                  })}
                />
                <SocialMediaV2>
                  <MultipleComponentIterator
                    iterator={instagramSocialMedia.nodes}
                    components={[
                      {
                        component: <SocialMediaV2.Platform />,
                        propMap: { key: "_id", url: "link", platform: "type" },
                        children: [
                          {
                            component: <SocialMediaV2.Icon />,
                            propMap: { icon: "type" },
                          },
                        ],
                      },
                    ]}
                  />
                </SocialMediaV2>
              </div>
              <MarkdownContent
                className={"locations-gallery-description"}
                content={getComponentContentNodeContent({
                  components: fishermanBusinessWebsitePage.components,
                  componentId: "image_gallery_section_description",
                })}
              />
              <PaginatedGrid
                rows={3}
                columns={5}
                className={"offset-images"}
                mobile={{ columns: 2, rows: 4, stackable: false }}
                items={multipleComponentIterator({
                  iterator: getComponentFiles({
                    components: fishermanBusinessWebsitePage.components,
                    componentName: "ImageCollection",
                  }),
                  components: [
                    {
                      component: (
                        <SlideReveal
                          direction={"up"}
                          fade={true}
                          triggerOnce={true}
                        />
                      ),
                      children: [
                        {
                          component: (
                            <Image
                              background={true}
                              style={{ paddingTop: "100%", height: "100%" }}
                            />
                          ),
                          propMap: { src: "__all__" },
                        },
                      ],
                    },
                  ],
                })}
              />
            </Grid.Column>
          </Grid>
        </PageLayout>
      </WebsiteLayout>
    );
  }
}

export const query = graphql`
  query {
    fishermanBusiness {
      name
    }
    fishermanBusinessWebsitePage(pageType: { eq: "Locations" }) {
      title
      description
      components {
        fastId
        componentIdentifier
        order
        files {
          file
          altText
          gatsbyFile {
            childImageSharp {
              gatsbyImageData(layout: FULL_WIDTH)
            }
          }
        }
        contentNodes {
          content
        }
      }
      seoTitle
    }
    allFishermanBusinessLocation {
      nodes {
        _id
        street
        city
        state
        zipCode
        name
        featuredImage
      }
    }
    allFishermanBusinessFile {
      nodes {
        _id
        file
        description
        gatsbyFile {
          childImageSharp {
            gatsbyImageData(layout: FULL_WIDTH)
          }
        }
      }
    }
    instagramSocialMedia: allFishermanBusinessSocialMedia(
      filter: { type: { eq: "instagram" } }
    ) {
      nodes {
        _id
        link
        type
      }
    }
  }
`;
